import React, { useMemo, useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { FileText, ChevronDown, X as XIcon, Search } from 'lucide-react';

/**
 * OrderFilter — multi-select popover for filtering by Order No.
 *
 * Mirrors the CustomerFilter / ReferenceFilter pattern so it feels native.
 * Options are the distinct orders present in the current data set; the caller
 * owns ``value`` (array of order ids) and ``onChange``.
 *
 * Props:
 *   options       {id, order_no, title?}[]  distinct orders to choose from
 *   value         string[]      currently selected order ids
 *   onChange      (next: string[]) => void
 *   counts        Map<id, number>  optional per-order match counts
 *   testIdPrefix  string        data-testid namespace
 */
export function OrderFilter({
  options = [],
  value = [],
  onChange,
  counts,
  testIdPrefix = 'order-filter',
}) {
  const [q, setQ] = useState('');
  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const sortedOptions = useMemo(() => {
    const seen = new Map();
    for (const o of (options || [])) {
      if (o && o.id && !seen.has(o.id)) {
        seen.set(o.id, { order_no: o.order_no || 'Order', title: o.title || '' });
      }
    }
    return Array.from(seen.entries())
      .map(([id, meta]) => ({ id, ...meta }))
      .sort((a, b) => String(a.order_no).localeCompare(String(b.order_no), undefined, { numeric: true, sensitivity: 'base' }));
  }, [options]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return sortedOptions;
    return sortedOptions.filter(
      (o) => String(o.order_no).toLowerCase().includes(term) || (o.title || '').toLowerCase().includes(term),
    );
  }, [sortedOptions, q]);

  const toggle = (id) => {
    const cur = new Set(value || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    onChange(Array.from(cur));
  };

  const active = (value || []).length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1.5 h-9 rounded-md border px-3 text-[12.5px] transition-colors ${
            active
              ? 'border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100'
              : 'border-[var(--divider)] bg-white text-slate-700 hover:bg-slate-50'
          }`}
          data-testid={`${testIdPrefix}-trigger`}
          title="Filter by Order No."
        >
          <FileText size={13} />
          <span>{active ? `Order · ${(value || []).length} selected` : 'All orders'}</span>
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start" data-testid={`${testIdPrefix}-content`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Order No.</div>
          {active && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid={`${testIdPrefix}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        {sortedOptions.length > 8 && (
          <div className="relative mb-2">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search orders…"
              className="h-8 pl-7 text-[12.5px]"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
        )}
        <div className="max-h-60 overflow-y-auto grid grid-cols-1 gap-1">
          {filtered.length === 0 && (
            <div className="text-[11px] text-slate-500 py-2 text-center">No orders.</div>
          )}
          {filtered.map((o) => {
            const isOn = selectedSet.has(o.id);
            const count = counts ? (counts.get(o.id) || 0) : null;
            return (
              <label
                key={o.id}
                className={`flex items-center justify-between gap-2 rounded-md px-2 py-1.5 cursor-pointer border ${
                  isOn ? 'bg-sky-50 border-sky-200' : 'border-transparent hover:bg-slate-50'
                }`}
                data-testid={`${testIdPrefix}-option-${o.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Checkbox
                    checked={isOn}
                    onCheckedChange={() => toggle(o.id)}
                    data-testid={`${testIdPrefix}-option-${o.id}-checkbox`}
                  />
                  <span className="min-w-0">
                    <span className="text-sm font-mono tabular-nums text-slate-800">{o.order_no}</span>
                    {o.title ? <span className="block text-[11px] text-slate-500 truncate">{o.title}</span> : null}
                  </span>
                </div>
                {count != null && (
                  <span className="text-[11px] font-mono tabular-nums text-slate-500 shrink-0">{count}</span>
                )}
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * OrderFilterChips — removable chips for the selected orders.
 */
export function OrderFilterChips({ value = [], labelById = {}, onChange, testIdPrefix = 'order-filter-chip' }) {
  if (!Array.isArray(value) || value.length === 0) return null;
  const remove = (id) => onChange((value || []).filter((x) => x !== id));
  return (
    <>
      {value.map((id) => (
        <span
          key={id}
          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium bg-sky-50 text-sky-700 border-sky-200"
          data-testid={`${testIdPrefix}-${id}`}
        >
          <span className="text-[10px] uppercase tracking-wide opacity-70">Order</span>
          <span className="font-mono tabular-nums truncate max-w-[120px]">{labelById[id] || 'Order'}</span>
          <button
            type="button"
            onClick={() => remove(id)}
            className="ml-0.5 inline-flex items-center rounded-full hover:bg-black/10 p-0.5 transition-colors"
            aria-label="Remove order filter"
            data-testid={`${testIdPrefix}-${id}-remove`}
          >
            <XIcon size={11} />
          </button>
        </span>
      ))}
    </>
  );
}
